import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import store from './app/store';
import { useAppSelector } from './app/hooks';
import { useLazyGetSearchResultsQuery } from './data/api/api';
import { ISearchResult, GetSearchResultsResponse, SearchConfig } from './interfaces/types';
import ReactPaginate from 'react-paginate';
import { SearchFilterCategories } from './components/search-filter-categories';
import { SearchResultsList } from './components/search-results-list';
import { SearchForm } from './components/search-form';
import { SearchFilterContentType } from './components/search-filter-contenttype';
import { SearchShowingText } from './components/search-showing-text';
import { SearchOrderBy } from './components/search-order-by';
import { Loader } from '../../Static/javascript/global/components/loader';

// Helper to Parse and Update URl Params
const setQueryParams = (params: Record<string, string | undefined>) => {
    const searchParams = new URLSearchParams(window.location.search);
    Object.entries(params).forEach(([key, value]) => {
        if (value) {
            searchParams.set(key, value);
        } else {
            searchParams.delete(key);
        }
    });
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, '', newUrl);
};

const getQueryParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
        keyword: searchParams.get("keyword") || "",
        categories: searchParams.get("categories")?.split(",").filter(c => c) || [],
        contentType: searchParams.get("contentTypes")?.split(",").filter(c => c) || [],
        orderBy: searchParams.get("orderBy") || ""
    };
};

const SearchPageApp: React.FC = () => {
    const appRef = useRef<HTMLDivElement>(null);
    const searchConfig: SearchConfig = useAppSelector((state) => state.searchConfig);
    const [searchCategories, setSearchCategories] = useState<string[]>([]);
    const [searchContentTypes, setSearchContentTypes] = useState<string[]>([]);
    const [orderBy, setOrderBy] = useState<string>('');
    const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchIsBeingPerformed, setSearchIsBeingPerformed] = useState(false);
    const searchTermIsValid = searchTerm != undefined && searchTerm.trim() !== '';
    const [expandedMobileFilters, setExpandedMobileFilters] = useState(false);
    const [triggerSearch, {
        data: searchResultsData = {} as GetSearchResultsResponse,
        isFetching: searchResultsFetching = false,
        isLoading: searchResultsLoading = false,
        error: searchResultsError = null
    }] = useLazyGetSearchResultsQuery();
    const searchResultsItems: ISearchResult[] = searchResultsData?.results ?? [];
    const itemsPerPage = searchResultsData ? searchConfig.pageSize : 10;
    const searchPageId = searchConfig.searchPageId;
    const [itemOffset, setItemOffset] = useState(0);
    const endOffset = itemOffset + itemsPerPage;
    const currentItems = searchResultsItems && searchResultsItems.length > 0 ? searchResultsItems.slice(itemOffset, endOffset) : null;
    const pageCount = searchResultsItems && searchResultsItems.length > 0 ? Math.ceil(searchResultsItems.length / itemsPerPage) : 0;
    // Invoke when user click to request another page.
    const handlePageClick = (event) => {
        const newOffset = event.selected * itemsPerPage;
        setItemOffset(newOffset);
        if (appRef.current) {
            const navContainer = document.getElementById('navContainer');
            const navHeight = navContainer ? navContainer.offsetHeight : 0;
            const appTop = appRef.current.getBoundingClientRect().top + window.scrollY;
            // Scroll to app position minus nav height
            window.scrollTo({
                top: appTop - navHeight,
                behavior: 'smooth',
            });
        }
    };


    const performSearch = () => {
        setItemOffset(0);//Reset pagination offset to 0
        if (searchTermIsValid) {
            const searchCategoriesCommaSeparated = searchCategories.length > 0 ? searchCategories.join(",") : "";
            const searchContentTypeCommaSeparated = searchContentTypes.length > 0 ? searchContentTypes.join(",") : "";
            setQueryParams({
                keyword: searchTerm,
                categories: searchCategoriesCommaSeparated,
                contentType: searchContentTypeCommaSeparated,
                orderBy: orderBy
            });

            triggerSearch({
                searchPageId: searchPageId,
                keyword: searchTerm,
                categories: searchCategoriesCommaSeparated,
                contentType: searchContentTypeCommaSeparated,
                orderBy: orderBy
            });
            setSearchPerformed(true);
        }
    }

    const updateCategories = (checkbox: HTMLInputElement, value: string) => {
        if (checkbox.checked)
            setSearchCategories((prevCategories) => [...prevCategories, value]);
        else
            setSearchCategories((prevCategories) => prevCategories.filter((category) => category !== value));
    }

    const updateContentType = (checkbox: HTMLInputElement, value: string) => {
        if (checkbox.checked) {
            setSearchContentTypes((prevContentTypes) => [...prevContentTypes, value]);
            if (value === "productiondetailpage") {
                setOrderBy("datedesc");
            }
        }
        else {
            setSearchContentTypes((prevContentTypes) => prevContentTypes.filter((contentType) => contentType !== value));
        }
    }

    const updateOrderBy = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOrderBy((event.target as HTMLInputElement).value)
    }

    const resetFilters = () => {
        setSearchContentTypes([]);
        setSearchCategories([]);
        setOrderBy('');
        setQueryParams({ keyword: searchTerm });
    }

    //Sync state from URL on load & on browser back/forward
    useEffect(() => {
        const syncFromUrl = () => {
            const params = getQueryParams();

            setSearchTerm(params.keyword || "");
            setSearchCategories(params.categories || []);
            setSearchContentTypes(params.contentType || []);
            setOrderBy(params.orderBy || "");

            if (params.keyword || params.categories.length > 0 || params.contentType || params.orderBy) {
                if (params.keyword != "") {
                    setSearchPerformed(true);
                    triggerSearch({
                        searchPageId: searchPageId,
                        keyword: params.keyword,
                        categories: params.categories.join(","),
                        contentType: params.contentType,
                        orderBy: params.orderBy
                    });
                }
            }
        };

        syncFromUrl(); //1st load
        window.addEventListener("popstate", syncFromUrl);

        return () => {
            window.removeEventListener("popstate", syncFromUrl);
        };
    }, [searchPageId, triggerSearch]);

    //Run search when filters change
    useEffect(() => {
        if (searchPerformed && searchTermIsValid) {
            setItemOffset(0);
            performSearch();
        }
    }, [searchCategories, searchContentTypes, orderBy]);

    return (
        <div className="search-app" ref={appRef}>
            <SearchForm placeholder={searchConfig.searchPlaceholder} performSearch={performSearch} setSearchTerm={setSearchTerm} />
            <div className="search-desktop">
                <SearchOrderBy updateOrderBy={updateOrderBy} orderBy={orderBy} uniqueId={'sortOrderDesktop'} />
            </div>
            <div className="search-app-inner">
                <div className="search-filters">
                    <div className="search-filters-header search-desktop">
                        <h2>Filters</h2>

                        <button className="search-filters-reset" onClick={() => {
                            resetFilters();
                        }}>
                            {searchConfig.resetFiltersLabel}
                        </button>
                    </div>
                    <div className="search-filters-header search-mobile">
                        <h2>Filters</h2>
                        <button className="search-filters-header-trigger" aria-controls={`searchFiltersPanel`} aria-expanded={expandedMobileFilters} onClick={() => {
                            setExpandedMobileFilters(!expandedMobileFilters)
                        }}>
                            <span className="visually-hidden">
                                Toggle Filters
                            </span>
                        </button>
                    </div>
                    <div className={`search-filters-panel ${expandedMobileFilters ? "show" : ""}`} id={`searchFiltersPanel`}>
                        <div className="search-filters-panel-inner">
                            <div className="search-mobile">
                                <SearchOrderBy updateOrderBy={updateOrderBy} orderBy={orderBy} uniqueId={'sortOrderMobile'} />
                            </div>
                            <SearchFilterContentType
                                filterByPageTypeLabel={searchConfig.filterByPageTypeLabel}
                                searchContentType={searchContentTypes}
                                updateContentType={updateContentType}
                                contentTypes={searchConfig.searchFilters.filter(item => item.name === "Content Types")[0].values}
                            />
                            <SearchFilterCategories
                                categoriesFilterLabel={searchConfig.categoriesFilterLabel}
                                categories={searchConfig.searchFilters.filter(item => item.name === "Categories")[0].values}
                                searchCategories={searchCategories}
                                updateCategories={updateCategories} />
                        </div>
                    </div>
                </div>
                <div className="search-listing">
                    {
                        (searchResultsLoading || searchResultsFetching) && (
                            <Loader loaderImageUrl={searchConfig.loaderImageUrl} />
                        )
                    }
                    {searchResultsError && <p className="error">Something went wrong while fetching results.</p>}

                    {searchPerformed && currentItems?.length > 0 && !searchResultsLoading && !searchResultsFetching ? (
                        <SearchResultsList results={currentItems} />
                    ) : null}

                    {searchPerformed && currentItems === null && !searchResultsLoading && !searchResultsFetching ? <p>No results found</p> : null}

                    {pageCount > 1 && searchPerformed && currentItems?.length > 0 && !searchResultsLoading && !searchResultsFetching ? (
                        <ReactPaginate
                            containerClassName="react-pagination"
                            breakLabel="..."
                            nextLabel="Next"
                            onPageChange={handlePageClick}
                            pageRangeDisplayed={4}
                            pageCount={pageCount}
                            previousLabel="Previous"
                            marginPagesDisplayed={0}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export function SearchPageAppInit(appRoot: HTMLElement) {
    const root = ReactDOM.createRoot(appRoot);
    root.render(
        <React.StrictMode>
            <Provider store={store}>
                <SearchPageApp />
            </Provider>
        </React.StrictMode>
    );
}

const articleListingAppRoot = document.getElementById('searchPage') as HTMLElement;
SearchPageAppInit(articleListingAppRoot);